import React, { useMemo } from 'react';
import { KEY_TO_FINGER, FINGER_COLORS } from '../../constants';

const SYMBOL_TO_KEY: { [key: string]: string } = {
  '!': '1', '@': '2', '#': '3', '$': '4', '%': '5',
  '^': '6', '&': '7', '*': '8', '(': '9', ')': '0',
  '_': '-', '+': '=', '{': '[', '}': ']', '|': '\\',
  ':': ';', '"': '\'', '<': ',', '>': '.', '?': '/',
  '~': '`'
};

interface OnScreenKeyboardProps {
  nextChar: string | null;
  isTutorial: boolean;
  lessonId: string | number | null;
  isError: boolean;
  lessonKeys: string[] | null;
  pressedKey: string | null; // New prop for visual feedback
}

const OnScreenKeyboard: React.FC<OnScreenKeyboardProps> = ({ nextChar, isTutorial, lessonId, isError, lessonKeys, pressedKey }) => {
  const keyboardLayout = [
    ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '='],
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', '[', ']', '\\'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ';', "'"],
    ['z', 'x', 'c', 'v', 'b', 'n', 'm', ',', '.', '/'],
    [' ']
  ];

  const lessonKeySet = useMemo(() => Array.isArray(lessonKeys) ? new Set(lessonKeys) : null, [lessonKeys]);

  const getKeyClass = (key: string): string => {
    const keyChar = key.toLowerCase();
    const nextCharLower = nextChar?.toLowerCase();
    const fingerIndex = KEY_TO_FINGER[keyChar];
    let baseClass = "h-10 md:h-12 flex-1 m-1 rounded-md font-mono font-semibold text-gray-700 bg-gray-200 flex items-center justify-center transition-all duration-100 shadow";
    
    if (key === ' ') baseClass += " flex-grow-[8]";

    let isHighlighted = false;

    if (isTutorial) {
        let tutorialKeys: string[] = [];
        const allPracticeKeys = ['a','s','d','f','g','h','j','k','l',';','\'','q','w','e','r','t','y','u','i','o','p','z','x','c','v','b','n','m',',','.','/', ' '];
        const allNumberKeys = ['1','2','3','4','5','6','7','8','9','0','-'];

        switch (lessonId) {
            case 'tutorial-1': case 'tutorial-15':
              tutorialKeys = ['a', 's', 'd', 'f', 'j', 'k', 'l', ';', ' ']; break;
            case 'tutorial-2':
              tutorialKeys = ['j', 'u', ' ']; break;
            case 'tutorial-3': case 'tutorial-4': case 'tutorial-5': case 'tutorial-6':
              tutorialKeys = ['f', 'j', ' ']; break;
            case 'tutorial-7': case 'tutorial-8':
              tutorialKeys = ['d', 'k', ' ']; break;
            case 'tutorial-9':
              tutorialKeys = ['f', 'j', 'd', 'k', ' ']; break;
            case 'tutorial-10': case 'tutorial-11':
              tutorialKeys = ['s', 'l', ' ']; break;
            case 'tutorial-12':
              tutorialKeys = ['s', 'l', 'd', 'k', 'f', 'j', ' ']; break;
            case 'tutorial-13': case 'tutorial-14':
              tutorialKeys = ['a', ';', ' ']; break;
            case 'tutorial-16': case 'tutorial-17':
              tutorialKeys = ['f', 'g', 'j', 'h', ' ']; break;
            case 'tutorial-18': case 'tutorial-19':
              tutorialKeys = ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ' ']; break;
            case 'tutorial-20': case 'tutorial-21': case 'tutorial-24':
              tutorialKeys = ['f', 'r', 'j', 'u', ' ']; break;
            case 'tutorial-22': case 'tutorial-25': case 'tutorial-26':
              tutorialKeys = ['a', 's', 'd', 'f', 'r', 'g', 'h', 'j', 'u', 'k', 'l', ' ']; break;
            case 'tutorial-23': case 'tutorial-27': case 'tutorial-28': case 'tutorial-29':
              tutorialKeys = ['d', 'e', 'k', 'i', ' ']; break;
            case 'tutorial-30':
              tutorialKeys = ['a','s','d','f','g','h','j','k','l',';','q','w','e','r','t','y','u','i','o','p', ' ']; break;
            case 'tutorial-31':
              tutorialKeys = ['a','s','d','f','j','k','l',';','r','u','e','i', ' ']; break;
            case 'tutorial-32':
              tutorialKeys = ['q','w','e','r','t',' ']; break;
            case 'tutorial-33':
              tutorialKeys = ['p','o','i','u','y',' ']; break;
            case 'tutorial-34':
              tutorialKeys = ['r','u','e','i', ' ']; break;
            case 'tutorial-35': case 'tutorial-36': case 'tutorial-37':
              tutorialKeys = ['w','o','s','l',' ']; break;
            case 'tutorial-38':
              tutorialKeys = ['r','u','e','i','w','o',' ']; break;
            case 'tutorial-39': case 'tutorial-40': case 'tutorial-41':
              tutorialKeys = ['q','y','a','j',' ']; break;
            case 'tutorial-42': case 'tutorial-43': case 'tutorial-44':
               tutorialKeys = ['p','t',';','f',' ']; break;
            case 'tutorial-45': case 'tutorial-47': case 'tutorial-48': case 'tutorial-49': case 'tutorial-50':
              tutorialKeys = ['a','s','d','f','g','h','j','k','l',';','q','w','e','r','t','y','u','i','o','p', ' ']; break;
            case 'tutorial-46':
              tutorialKeys = ['a','s','d','f','g','h','j','k','l',';','q','w','e','r','t','y','u','i','o','p', ' ']; break;
            case 'tutorial-51':
              tutorialKeys = ['1','0','a',';', ' ']; break;
            case 'tutorial-52': case 'tutorial-53': case 'tutorial-54':
              tutorialKeys = ['v','m','f','j', ' ']; break;
            case 'tutorial-55': case 'tutorial-56':
              tutorialKeys = ['j', 'm', 'n', 'u', 'y', 'h', 'f', 'v', 'b', 'r', 't', 'g', ' ']; break;
            case 'tutorial-57': case 'tutorial-58': case 'tutorial-59':
              tutorialKeys = ['c',',','d','k', ' ']; break;
            case 'tutorial-60': case 'tutorial-61':
              tutorialKeys = ['k','i',',','d','e','c', ' ']; break;
            case 'tutorial-62':
              tutorialKeys = ['v','m','c',',','h','a','e', ' ']; break;
            case 'tutorial-63': case 'tutorial-87': case 'tutorial-88':
              tutorialKeys = allPracticeKeys; break;
            case 'tutorial-64': case 'tutorial-65': case 'tutorial-66':
              tutorialKeys = ['x','.','s','l', ' ']; break;
            case 'tutorial-67': case 'tutorial-68':
              tutorialKeys = ['l','o','.','s','w','x', ' ']; break;
            case 'tutorial-69': case 'tutorial-70': case 'tutorial-71':
              tutorialKeys = ['z','/','a',';', ' ']; break;
            case 'tutorial-72': case 'tutorial-73':
              tutorialKeys = [';','p','/','a','q','z', ' ']; break;
            case 'tutorial-74': case 'tutorial-81': case 'tutorial-82': case 'tutorial-83':
              tutorialKeys = allPracticeKeys; break;
            case 'tutorial-75': case 'tutorial-76': case 'tutorial-77':
              tutorialKeys = ['b','n','f','j', ' ']; break;
            case 'tutorial-78': case 'tutorial-86':
              tutorialKeys = allPracticeKeys; break;
            case 'tutorial-79':
              tutorialKeys = ['z','x','c','v','b', ' ']; break;
            case 'tutorial-80':
              tutorialKeys = ['n','m',',','.','/', ' ']; break;
            case 'tutorial-84':
              tutorialKeys = ['j','m','n','u','y','h','k','i',',','l','o','.','p',';','/', ' ']; break;
            case 'tutorial-85':
              tutorialKeys = ['f','v','b','r','t','g','d','e','c','s','w','x','a','q','z', ' ']; break;
            case 'tutorial-89': case 'tutorial-90': case 'tutorial-91': case 'tutorial-92': 
            case 'tutorial-94': case 'tutorial-95': case 'tutorial-96': case 'tutorial-97': 
            case 'tutorial-98': case 'tutorial-101': case 'tutorial-102': case 'tutorial-103':
            case 'tutorial-104': case 'tutorial-106': case 'tutorial-107': case 'tutorial-108':
            case 'tutorial-109': case 'tutorial-110': case 'tutorial-112': case 'tutorial-113':
            case 'tutorial-114': case 'tutorial-115': case 'tutorial-116': case 'tutorial-118':
            case 'tutorial-119': case 'tutorial-120': case 'tutorial-121': case 'tutorial-122':
            case 'tutorial-124': case 'tutorial-125': case 'tutorial-126':
            case 'tutorial-127': case 'tutorial-128': case 'tutorial-129': case 'tutorial-130':
            case 'tutorial-131': case 'tutorial-132': case 'tutorial-133': case 'tutorial-134':
            case 'tutorial-135': case 'tutorial-136': case 'tutorial-137': case 'tutorial-138':
            case 'tutorial-177': case 'tutorial-188': case 'tutorial-189': case 'tutorial-190': case 'tutorial-191':
            case 'tutorial-192': case 'tutorial-193': case 'tutorial-194': case 'tutorial-195':
            case 'tutorial-196': case 'tutorial-197': case 'tutorial-198': case 'tutorial-199':
            case 'tutorial-200': case 'tutorial-201': case 'tutorial-202': case 'tutorial-203':
            case 'tutorial-204': case 'tutorial-205': case 'tutorial-206': case 'tutorial-207':
            case 'tutorial-208': case 'tutorial-209': case 'tutorial-210': case 'tutorial-211':
            case 'tutorial-212': case 'tutorial-213': case 'tutorial-214': case 'tutorial-215':
            case 'tutorial-216': case 'tutorial-217': case 'tutorial-218': case 'tutorial-219':
            case 'tutorial-220': case 'tutorial-221': case 'tutorial-222': case 'tutorial-223':
            case 'tutorial-224': case 'tutorial-225': case 'tutorial-226': case 'tutorial-227':
            case 'tutorial-228': case 'tutorial-229': case 'tutorial-230': case 'tutorial-231':
            case 'tutorial-232': case 'tutorial-233':
            case 'tutorial-234': case 'tutorial-235': case 'tutorial-236': case 'tutorial-237':
            case 'tutorial-238': case 'tutorial-239': case 'tutorial-240': case 'tutorial-241':
            case 'tutorial-242': case 'tutorial-243': case 'tutorial-244':
            case 'tutorial-275': case 'tutorial-276': case 'tutorial-277': case 'tutorial-278':
            case 'tutorial-279': case 'tutorial-280': case 'tutorial-281': case 'tutorial-282':
            case 'tutorial-283': case 'tutorial-284': case 'tutorial-285': case 'tutorial-286':
            case 'tutorial-287': case 'tutorial-288': case 'tutorial-289': case 'tutorial-290':
            case 'tutorial-291': case 'tutorial-292': case 'tutorial-293': case 'tutorial-294':
            case 'tutorial-295': case 'tutorial-296': case 'tutorial-297': case 'tutorial-298':
            case 'tutorial-299': case 'tutorial-300': case 'tutorial-301': case 'tutorial-302':
            case 'tutorial-303': case 'tutorial-304': case 'tutorial-305': case 'tutorial-306':
            case 'tutorial-307': case 'tutorial-308': case 'tutorial-309': case 'tutorial-310':
            case 'tutorial-311': case 'tutorial-312': case 'tutorial-313': case 'tutorial-314':
            case 'tutorial-315': case 'tutorial-316':
            case 'tutorial-347': case 'tutorial-348': case 'tutorial-349':
            case 'tutorial-350': case 'tutorial-351': case 'tutorial-352':
            case 'tutorial-353': case 'tutorial-354': case 'tutorial-355':
            case 'tutorial-356': case 'tutorial-357':
            case 'tutorial-358': case 'tutorial-359': case 'tutorial-360': case 'tutorial-361':
            case 'tutorial-362': case 'tutorial-363': case 'tutorial-364': case 'tutorial-365':
            case 'tutorial-366': case 'tutorial-367': case 'tutorial-368': case 'tutorial-369':
            case 'tutorial-370': case 'tutorial-371': case 'tutorial-372': case 'tutorial-373':
            case 'tutorial-374': case 'tutorial-375': case 'tutorial-376': case 'tutorial-377':
            case 'tutorial-378': case 'tutorial-379': case 'tutorial-380': case 'tutorial-381':
            case 'tutorial-382': case 'tutorial-383': case 'tutorial-384': case 'tutorial-385':
            case 'tutorial-386': case 'tutorial-387': case 'tutorial-388':
            case 'tutorial-419': case 'tutorial-420': case 'tutorial-421': case 'tutorial-422':
            case 'tutorial-423': case 'tutorial-424': case 'tutorial-425': case 'tutorial-426':
            case 'tutorial-427': case 'tutorial-428': case 'tutorial-429': case 'tutorial-430':
            case 'tutorial-431': case 'tutorial-432': case 'tutorial-433': case 'tutorial-434':
            case 'tutorial-435': case 'tutorial-436': case 'tutorial-437': case 'tutorial-438':
            case 'tutorial-439': case 'tutorial-440': case 'tutorial-441': case 'tutorial-442':
            case 'tutorial-443': case 'tutorial-444': case 'tutorial-445': case 'tutorial-446':
            case 'tutorial-447': case 'tutorial-448': case 'tutorial-449': case 'tutorial-450':
            case 'tutorial-451': case 'tutorial-452': case 'tutorial-453': case 'tutorial-454':
            case 'tutorial-455': case 'tutorial-456': case 'tutorial-457': case 'tutorial-458':
            case 'tutorial-459': case 'tutorial-460':
            case 'tutorial-461': case 'tutorial-462': case 'tutorial-463': case 'tutorial-464': case 'tutorial-465':
            case 'tutorial-466': case 'tutorial-467': case 'tutorial-468': case 'tutorial-469': case 'tutorial-470':
            case 'tutorial-471': case 'tutorial-472': case 'tutorial-473': case 'tutorial-474': case 'tutorial-475':
            case 'tutorial-476': case 'tutorial-477': case 'tutorial-478': case 'tutorial-479': case 'tutorial-480':
            case 'tutorial-481': case 'tutorial-482': case 'tutorial-483': case 'tutorial-484': case 'tutorial-485':
            case 'tutorial-486': case 'tutorial-487': case 'tutorial-488': case 'tutorial-489': case 'tutorial-490':
            case 'tutorial-491': case 'tutorial-492': case 'tutorial-493': case 'tutorial-494': case 'tutorial-495':
            case 'tutorial-496': case 'tutorial-497': case 'tutorial-498': case 'tutorial-499': case 'tutorial-500':
            case 'tutorial-501': case 'tutorial-502': case 'tutorial-503': case 'tutorial-504': case 'tutorial-505':
            case 'tutorial-506': case 'tutorial-507': case 'tutorial-508': case 'tutorial-509': case 'tutorial-510':
            case 'tutorial-511': case 'tutorial-512': case 'tutorial-513': case 'tutorial-514': case 'tutorial-515':
            case 'tutorial-516': case 'tutorial-517': case 'tutorial-518': case 'tutorial-519': case 'tutorial-520':
            case 'tutorial-521': case 'tutorial-522': case 'tutorial-523': case 'tutorial-524': case 'tutorial-525':
            case 'tutorial-526': case 'tutorial-527': case 'tutorial-528': case 'tutorial-529': case 'tutorial-530':
            case 'tutorial-531': case 'tutorial-532': case 'tutorial-533': case 'tutorial-534': case 'tutorial-535':
            case 'tutorial-536': case 'tutorial-537': case 'tutorial-538': case 'tutorial-539': case 'tutorial-540':
            case 'tutorial-541': case 'tutorial-542': case 'tutorial-543': case 'tutorial-544': case 'tutorial-545':
            case 'tutorial-546': case 'tutorial-547': case 'tutorial-548': case 'tutorial-549': case 'tutorial-550':
            case 'tutorial-551': case 'tutorial-552': case 'tutorial-553': case 'tutorial-554': case 'tutorial-555':
            case 'tutorial-556': case 'tutorial-557': case 'tutorial-558': case 'tutorial-559': case 'tutorial-560':
            case 'tutorial-561': case 'tutorial-562': case 'tutorial-563': case 'tutorial-564': case 'tutorial-565':
            case 'tutorial-566': case 'tutorial-567': case 'tutorial-568': case 'tutorial-569': case 'tutorial-570':
            case 'tutorial-571': case 'tutorial-572': case 'tutorial-573': case 'tutorial-574': case 'tutorial-575':
            case 'tutorial-576': case 'tutorial-577': case 'tutorial-578': case 'tutorial-579': case 'tutorial-580':
            case 'tutorial-581': case 'tutorial-582': case 'tutorial-583': case 'tutorial-584': case 'tutorial-585':
            case 'tutorial-586': case 'tutorial-587': case 'tutorial-588': case 'tutorial-589': case 'tutorial-590':
            case 'tutorial-591': case 'tutorial-592': case 'tutorial-593': case 'tutorial-594': case 'tutorial-595':
            case 'tutorial-596': case 'tutorial-597': case 'tutorial-598': case 'tutorial-599': case 'tutorial-600':
            case 'tutorial-601': case 'tutorial-602': case 'tutorial-603': case 'tutorial-604': case 'tutorial-605':
            case 'tutorial-606': case 'tutorial-607': case 'tutorial-608': case 'tutorial-609': case 'tutorial-610':
            case 'tutorial-611': case 'tutorial-612': case 'tutorial-613': case 'tutorial-614': case 'tutorial-615':
            case 'tutorial-616': case 'tutorial-617': case 'tutorial-618': case 'tutorial-619': case 'tutorial-620':
            case 'tutorial-621': case 'tutorial-622': case 'tutorial-623': case 'tutorial-624': case 'tutorial-625':
            case 'tutorial-626': case 'tutorial-627': case 'tutorial-628': case 'tutorial-629': case 'tutorial-630':
            case 'tutorial-631': case 'tutorial-632': case 'tutorial-633': case 'tutorial-634': case 'tutorial-635':
            case 'tutorial-636': case 'tutorial-637': case 'tutorial-638': case 'tutorial-639': case 'tutorial-640':
            case 'tutorial-641': case 'tutorial-642': case 'tutorial-643': case 'tutorial-644': case 'tutorial-645':
            case 'tutorial-646': case 'tutorial-647': case 'tutorial-648': case 'tutorial-649': case 'tutorial-650':
            case 'tutorial-651': case 'tutorial-652': case 'tutorial-653': case 'tutorial-654': case 'tutorial-655':
            case 'tutorial-656': case 'tutorial-657': case 'tutorial-658': case 'tutorial-659': case 'tutorial-660':
            case 'tutorial-661': case 'tutorial-662': case 'tutorial-663': case 'tutorial-664': case 'tutorial-665':
            case 'tutorial-666': case 'tutorial-667': case 'tutorial-668': case 'tutorial-669': case 'tutorial-670':
                tutorialKeys = allPracticeKeys; break;
            case 'tutorial-93': case 'tutorial-105': case 'tutorial-117':
              tutorialKeys = ['y','u','i','o','p','h','j','k','l',';','\'','n','m',',','.','/', ' ']; break; // R Hand
            case 'tutorial-99': case 'tutorial-111': case 'tutorial-123':
              tutorialKeys = ['q','w','e','r','t','a','s','d','f','g','z','x','c','v','b', ' ']; break; // L Hand
            case 'tutorial-100':
              tutorialKeys = ['1','2','3','4','5','6','7','8','9','0', ' ']; break; // Numbers
            case 'tutorial-139': case 'tutorial-140': case 'tutorial-141':
                tutorialKeys = ['f','j',' ']; break;
            case 'tutorial-142': case 'tutorial-143': case 'tutorial-144':
                tutorialKeys = ['d','k',' ']; break;
            case 'tutorial-145':
                tutorialKeys = ['f','j','d','k',' ']; break;
            case 'tutorial-146': case 'tutorial-147': case 'tutorial-148':
                tutorialKeys = ['s','l',' ']; break;
            case 'tutorial-149': case 'tutorial-150': case 'tutorial-151':
                tutorialKeys = ['a',';',':',' ']; break;
            case 'tutorial-152':
                tutorialKeys = ['s','l','a',':',' ']; break;
            case 'tutorial-153': case 'tutorial-154': case 'tutorial-155':
                tutorialKeys = ['g','h',' ']; break;
            case 'tutorial-156': case 'tutorial-157': case 'tutorial-158':
                tutorialKeys = ['t','y',' ']; break;
            case 'tutorial-159':
                tutorialKeys = ['g','h','t','y',' ']; break;
            case 'tutorial-160': case 'tutorial-161': case 'tutorial-162':
                tutorialKeys = ['r','u',' ']; break;
            case 'tutorial-163': case 'tutorial-164': case 'tutorial-165':
                tutorialKeys = ['e','i',' ']; break;
            case 'tutorial-166':
                tutorialKeys = ['r','u','e','i',' ']; break;
            case 'tutorial-167': case 'tutorial-168': case 'tutorial-169':
                tutorialKeys = ['w','o',' ']; break;
            case 'tutorial-170': case 'tutorial-171': case 'tutorial-172':
                tutorialKeys = ['q','p',' ']; break;
            case 'tutorial-173':
                tutorialKeys = ['w','o','q','p',' ']; break;
            case 'tutorial-174': case 'tutorial-175': case 'tutorial-176':
                tutorialKeys = ['v','m',' ']; break;
            case 'tutorial-178': case 'tutorial-179': case 'tutorial-180':
                tutorialKeys = ['c','?',' ']; break;
            case 'tutorial-181':
                tutorialKeys = ['v','m','c','?',' ']; break;
            case 'tutorial-182': case 'tutorial-183': case 'tutorial-184':
                tutorialKeys = ['z','n',' ']; break;
            case 'tutorial-185': case 'tutorial-186': case 'tutorial-187':
                tutorialKeys = ['x','b',' ']; break;
            // Numbers
            case 'tutorial-245': case 'tutorial-246': case 'tutorial-247':
                tutorialKeys = ['4','7', ' ']; break;
            case 'tutorial-248': case 'tutorial-249': case 'tutorial-250':
                tutorialKeys = ['3','8', ' ']; break;
            case 'tutorial-251': case 'tutorial-252': case 'tutorial-253':
                tutorialKeys = ['2','9', ' ']; break;
            case 'tutorial-254': case 'tutorial-255': case 'tutorial-256':
                tutorialKeys = ['1','0', ' ']; break;
            case 'tutorial-257': case 'tutorial-258': case 'tutorial-259':
                tutorialKeys = ['5','6', ' ']; break;
            case 'tutorial-260':
                tutorialKeys = [...allNumberKeys, ' ']; break;
            case 'tutorial-261': case 'tutorial-264':
                tutorialKeys = ['f','j','4','5','6','7', ' ']; break;
            case 'tutorial-262': case 'tutorial-265':
                tutorialKeys = ['d','s','k','l','2','3','8','9', ' ']; break;
            case 'tutorial-263': case 'tutorial-266':
                tutorialKeys = ['a',';','f','j','1','5','6','0', ' ']; break;
            case 'tutorial-267': case 'tutorial-271':
                tutorialKeys = ['1','2','3','4','5', ' ']; break;
            case 'tutorial-268': case 'tutorial-272':
                tutorialKeys = ['6','7','8','9','0', ' ']; break;
            case 'tutorial-269':
                tutorialKeys = ['a','s','d','f','1','2','3','4','5', ' ']; break;
            case 'tutorial-270':
                tutorialKeys = ['j','k','l',';','6','7','8','9','0', ' ']; break;
            case 'tutorial-273':
                tutorialKeys = [...allNumberKeys, 'a','s','d','f','j','k','l',';', ' ']; break;
            case 'tutorial-274':
                tutorialKeys = [...allNumberKeys, ...allPracticeKeys, '-']; break;
            // Symbols
            case 'tutorial-317': case 'tutorial-318': case 'tutorial-319':
                tutorialKeys = ['4','7','f', 'j', ' ']; break;
            case 'tutorial-320': case 'tutorial-321': case 'tutorial-322':
                tutorialKeys = ['3','8','d', 'k', ' ']; break;
            case 'tutorial-323': case 'tutorial-324': case 'tutorial-325':
                tutorialKeys = ['2','9','s', 'l', ' ']; break;
            case 'tutorial-326': case 'tutorial-327': case 'tutorial-328':
                tutorialKeys = ['1','0','a', ';', ' ']; break;
            case 'tutorial-329': case 'tutorial-330': case 'tutorial-331':
                tutorialKeys = ['5','6','f', 'j', ' ']; break;
            case 'tutorial-332': case 'tutorial-345': case 'tutorial-346':
                tutorialKeys = ['1','2','3','4','5','6','7','8','9','0', ...allPracticeKeys]; break;
            case 'tutorial-333': tutorialKeys = ['f','4', ' ']; break;
            case 'tutorial-334': tutorialKeys = ['d','3', ' ']; break;
            case 'tutorial-335': tutorialKeys = ['s','2', ' ']; break;
            case 'tutorial-336': tutorialKeys = ['a','1', ' ']; break;
            case 'tutorial-337': tutorialKeys = ['f','5', ' ']; break;
            case 'tutorial-338': tutorialKeys = ['j','7', ' ']; break;
            case 'tutorial-339': tutorialKeys = ['k','8', ' ']; break;
            case 'tutorial-340': tutorialKeys = ['l','9', ' ']; break;
            case 'tutorial-341': tutorialKeys = [';','0', ' ']; break;
            case 'tutorial-342': tutorialKeys = ['j','6', ' ']; break;
            case 'tutorial-343':
                tutorialKeys = ['a','s','d','f','1','2','3','4','5', ' ']; break;
            case 'tutorial-344':
                tutorialKeys = ['j','k','l',';','6','7','8','9','0', ' ']; break;
            // More Symbols
            case 'tutorial-389': case 'tutorial-390': case 'tutorial-391':
                tutorialKeys = ['`', ' ']; break;
            case 'tutorial-392': case 'tutorial-393': case 'tutorial-394':
                tutorialKeys = ['-', '=', ' ']; break;
            case 'tutorial-395': case 'tutorial-396': case 'tutorial-397':
                tutorialKeys = ['-', '=', ' ']; break;
            case 'tutorial-398': case 'tutorial-399': case 'tutorial-400':
                tutorialKeys = ['[', ']', ' ']; break;
            case 'tutorial-401': case 'tutorial-402': case 'tutorial-403':
                tutorialKeys = ['[', ']', ' ']; break;
            case 'tutorial-404': case 'tutorial-405': case 'tutorial-406':
                tutorialKeys = ['[', ']', ' ']; break;
            case 'tutorial-407': case 'tutorial-408': case 'tutorial-409':
                tutorialKeys = ['\\', ' ']; break;
            case 'tutorial-410': case 'tutorial-411': case 'tutorial-412':
                tutorialKeys = [',', '.', ' ']; break;
            case 'tutorial-413': case 'tutorial-414': case 'tutorial-415': case 'tutorial-416': case 'tutorial-417': case 'tutorial-418':
                tutorialKeys = ['`', '-', '=', '[', ']', '\\', ',', '.', ' ']; break;
        }
        if (tutorialKeys.includes(keyChar)) isHighlighted = true;
    } else if (lessonKeySet) {
        if (lessonKeySet.has(keyChar)) isHighlighted = true;
    } else { // Game mode / default
        isHighlighted = true;
    }

    if(isHighlighted && fingerIndex !== undefined) {
         baseClass = `h-10 md:h-12 flex-1 m-1 rounded-md font-mono font-semibold text-white ${FINGER_COLORS[fingerIndex]} flex items-center justify-center transition-all duration-100 shadow-lg`;
         if (key === ' ') baseClass += " flex-grow-[8] opacity-80";
    } else {
        baseClass += " opacity-20";
    }

    if (keyChar === 'f' || keyChar === 'j') baseClass += " relative";
    if (keyChar === nextCharLower || (nextChar && keyChar === SYMBOL_TO_KEY[nextChar])) {
        baseClass += " scale-110 opacity-100 border-2 border-black";
        if (isError) baseClass += " animate-pulse bg-red-500 border-red-700";
    }

    const pressedKeyLower = pressedKey?.toLowerCase();
    if (keyChar === pressedKeyLower) {
        baseClass += " scale-95 brightness-75";
    }
    
    return baseClass;
  };

  return (
    <div className="w-full p-2 bg-gray-100 rounded-lg mt-4">
      {keyboardLayout.map((row, rowIndex) => (
        <div key={rowIndex} className="flex justify-center">
          {row.map((key) => (
            <div key={key} className={getKeyClass(key)}>
              {key === ' ' ? 'Space' : key}
              {(key === 'f' || key === 'j') && <span className="absolute bottom-1 w-1/3 h-0.5 bg-gray-600 opacity-50 rounded-full"></span>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default OnScreenKeyboard;